import {
  Box,
  Button,
  Chip,
  Grid,
  GridColumn,
  Text,
  ThemeProvider
} from "@modernatx/ui-kit-react";
import { useOktaAuth } from "@okta/okta-react";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import { UtilityCard } from "@/components/Card/UtilityCard";
import ExternalLinkModal from "@/components/ExternalLinkModal/ExternalLinkModal";
import { useModal } from "@/hooks/useModal";

import strandImage from "../assets/images/banner-strand-top.png";
import mrnaNewsIcon from "../assets/images/mrnaNewsIcon.png";
import prophylacticVaccinesIcon from "../assets/images/prophylactic-vaccines-icon.png";

const AuthHomepage = () => {
  const { authState } = useOktaAuth() || "";
  const isAuthenticated = authState?.isAuthenticated;
  const { handleOpenModal } = useModal();
  const history = useHistory();
  const [showExternalLinkModal, setShowExternalLinkModal] = useState(false);
  const [externalLink, setExternalLink] = useState("");

  const navigateOrLogin = (path: string): void => {
    if (isAuthenticated) {
      history.push(`/${path}`);
    } else {
      handleOpenModal(`/${path}`);
    }
  };

  const handleRedirect = (url: string) => {
    if (!localStorage.getItem("dontRemindMeExternal")) {
      setShowExternalLinkModal(true);
      setExternalLink(url);
    } else {
      window.open(url, "_blank");
      setExternalLink("");
    }
  };

  return (
    <ThemeProvider>
      <ExternalLinkModal
        isOpen={showExternalLinkModal}
        setShowExternalLinkModal={setShowExternalLinkModal}
        externalLink={externalLink}
      />
      <Box
        sx={{
          backgroundImage: `url(${strandImage})`,
          backgroundSize: "100% 140%",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center right",
          backgroundColor: "color-current-brand-950",
          py: "spacing-12",
          width: "100%",
          userSelect: "text"
        }}
      >
        <Grid>
          <GridColumn size={[4, 8, 8]}>
            <Text
              variant="h4"
              sx={{
                // @ts-ignore
                width: ["100%", null, "70%"],
                color: "white"
              }}
            >
              Reservations available for 2025-2026 Moderna COVID-19 Vaccine
              Formula*
            </Text>
            <Text sx={{ color: "white", mt: "spacing-3" }}>
              *Subject to FDA approval or emergency use authorization of
              2025-2026 formula
            </Text>
            <Box sx={{ width: "fit-content", mt: "spacing-6" }}>
              <Button
                size="large"
                onClick={() => navigateOrLogin("reservation")}
              >
                Reserve now
              </Button>
            </Box>
          </GridColumn>
        </Grid>
      </Box>
      <Box
        sx={{
          background:
            "linear-gradient(94deg, #06557D 2.73%, #0E6896 45.24%, #44A7D9 99.83%)",
          width: "100%",
          py: "spacing-8",
          alignItems: "center"
        }}
      >
        <Grid sx={{ alignContent: "center", alignItems: "center" }}>
          <GridColumn
            size={[4, 8, 6]}
            push={[0, 0, 3]}
            sx={{
              alignItems: "center",
              textAlign: "center",
              py: "spacing-7"
            }}
          >
            <Chip label="New" variant="neutral" />

            <Text variant="h5" sx={{ color: "white", mt: "spacing-6" }}>
              View all of your invoices and credits in one place
            </Text>
            <Text
              variant="body-1-regular"
              sx={{ color: "white", mt: "spacing-3" }}
            >
              {`Thanks to your feedback, you can now securely view and pay invoices as well as view your credits. Additional features like scheduling payments are planned for future updates. Click "View invoices" to get started.`}
            </Text>
            <Box sx={{ mt: "spacing-6" }}>
              <Button
                size="large"
                variant="outline"
                aria-label="arrow-right"
                icon="arrow-right"
                iconPosition="end"
                onClick={() =>
                  navigateOrLogin("your-account/invoices-and-payments")
                }
              >
                View invoices
              </Button>
            </Box>
          </GridColumn>
        </Grid>
      </Box>
      <Grid
        sx={{
          py: "spacing-9",
          gap: ["spacing-4", "spacing-none", null]
        }}
      >
        <UtilityCard
          header="Order Moderna products"
          icon="shopping-cart-simple"
          onClick={() => {
            navigateOrLogin("order-form");
          }}
        />
        <UtilityCard
          header="Submit your tax exemption certificates"
          icon="files"
          onClick={() => {
            handleRedirect("https://taxform.modernatx.com/");
          }}
        />
        <UtilityCard
          header="Pay an invoice as a guest"
          icon="credit-card"
          description="Click here to pay an invoice if you don’t have a ModernaDirect account. "
          onClick={() => {
            history.push("/invoice/pay");
          }}
        />
      </Grid>
      <Grid
        sx={{
          py: "spacing-9",
          gap: ["spacing-4", "spacing-none", null]
        }}
      >
        <GridColumn>
          <Text variant="h5">Resources</Text>
        </GridColumn>
        <UtilityCard
          description="For more information about Moderna’s mRNA platform"
          image={mrnaNewsIcon}
          actions={[
            {
              children: "Visit",
              variant: "outline",
              icon: "arrow-circle-right",
              iconPosition: "end",
              onClick: () => {
                handleRedirect(
                  "https://www.modernatx.com/en-US/power-of-mrna/science-of-mrna"
                );
              }
            }
          ]}
        />
        <UtilityCard
          description="For more information on the current Moderna COVID-19 vaccines"
          image={prophylacticVaccinesIcon}
          actions={[
            {
              children: "Visit",
              variant: "outline",
              icon: "arrow-circle-right",
              iconPosition: "end",
              onClick: () => {
                handleRedirect(
                  "https://products.modernatx.com/spikevax/6-months-to-11-years"
                );
              }
            }
          ]}
        />
      </Grid>
    </ThemeProvider>
  );
};

export default AuthHomepage;
